import { computed, inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AuthService, PlatformService } from '@evc/platform';
import { catchError, defer, distinctUntilChanged, filter, map, Observable, of, shareReplay, tap } from 'rxjs';

import { DialogNotification, FeatureFlagsConfigService, NotificationDialogComponent } from '@olympus/ngx-shared';
import { DialogService } from 'shared-modules-angular/dialog';
import { ILicensingService } from 'shared-modules-angular/licensing';
import { nonNullish, precondition } from 'shared-utils/data';

import { LicensedFeature } from '../../../core/config/feature-flags.config';
import { ConfigService } from '../../../core/services/config';

@Injectable({
  providedIn: 'root',
})
export class EvCLicenseService implements ILicensingService<LicensedFeature> {
  /**
   * Shared, single-flight license check: `isLicenseValid()` performs an HTTP/EVC call
   * and pops an "Invalid License" dialog as a side effect, so it must not be
   * resubscribed independently by every consumer. `defer` keeps the check lazy (it only
   * runs once actually subscribed, same as calling `isLicenseValid()` directly), and
   * `shareReplay(1)` (no `refCount`) then runs it once, ever, replaying the result to
   * everyone else.
   *
   * NOTE: `AppComponent.ngOnInit` still calls `isLicenseValid()` directly (changing that
   * call site touches a large, tightly-pinned spec suite, out of scope here), so it is
   * NOT deduplicated against this stream. Any new web consumer (e.g. a mode-availability
   * adapter feeding `FeatureFlagsService`) must use `licenseValid$`, and `AppComponent`
   * should be migrated to it too once that spec is updated, or the license will still be
   * checked (and its dialog possibly shown) twice.
   */
  public readonly licenseValid$: Observable<boolean>;

  /** Web only ever grants 'analysis' - there is no web-inspection license. See `ILicensingService`. */
  public readonly validLicenses$: Observable<LicensedFeature[]>;

  private readonly _platformService: PlatformService | null = null;
  private readonly _authService: AuthService | null = null;
  private readonly _licenseIds$: Observable<string[]> = of([]);

  constructor(
    private readonly _config: ConfigService,
    private readonly _dialogService: DialogService,
    private readonly _featureFlagsConfigService: FeatureFlagsConfigService,
  ) {
    if (this._config.isEvidentConnect) {
      this._authService = inject(AuthService);
      this._platformService = inject(PlatformService);

      this._licenseIds$ = toObservable(
        computed(
          () => {
            if (this._platformService?.ready() && this._authService?.isAuthenticated()) {
              return this._authService.licenseIds() || [];
            }

            return null;
          },
        ),
      ).pipe(
        filter((ids): ids is string[] => nonNullish(ids)),
      );
    }

    this.licenseValid$ = defer(() => this.isLicenseValid()).pipe(shareReplay({ bufferSize: 1, refCount: false }));
    this.validLicenses$ = this.licenseValid$.pipe(
      map(valid => (valid ? (['analysis'] satisfies LicensedFeature[]) : [])),
      catchError(() => of([])),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  public isLicenseValid(): Observable<boolean> {
    return this.getUserLicense().pipe(
      tap((userLicense) => {
        if (!userLicense) {
          const notification: DialogNotification = {
            headerTitle: 'Invalid License',
            titleIconName: 'exclamation-triangle',
            bodyMessage: `Your ${this._featureFlagsConfigService.appConfig.appName} license is invalid. Please contact your Evident sales rep. for further assistance.`,
          };

          this._dialogService.open(
            NotificationDialogComponent,
            {
              data: notification,
              height: NotificationDialogComponent.DEFAULT_HEIGHT,
              width: NotificationDialogComponent.DEFAULT_WIDTH,
              disableClose: true,
            },
          );
        }
      }),
    );
  }

  private getUserLicense(): Observable<boolean> {
    if (this._config.isEvidentConnect) {
      return this.getEvidentConnectLicense();
    }

    return of(false);
  }

  private getEvidentConnectLicense(): Observable<boolean> {
    precondition(this._platformService && this._authService, 'Evident Connect services are not available');

    // NOTE: Since EvidentConnect already validates the Client ID, as soon as there is a license, we are authenticated.
    return this._licenseIds$.pipe(
      // ATTENTION: When no licenses are available, the licenseIds is actually [""] not an empty array.
      map(ids => ids.some(id => id.trim().length > 0)),
      distinctUntilChanged(),
    );
  }
}
